'use client';

import { useSearchParams } from 'next/navigation';
import { ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingGradient } from '@provider-directory/components/loading-gradient/loading-gradient.component';
import { SortSelect } from '@provider-directory/components/sort-select/sort-select';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { useSearch } from '@provider-directory/contexts/ProviderSearchContext';
import { useCalendarHook } from '@provider-directory/hooks/useCalendar';
import { FilterBanner } from './filter-banner';
import { ProviderSearchCalendar } from './provider-search-calendar';
import { ProviderSearchFilters } from './provider-search-filters.component';
import { ProviderSearchResults } from './provider-search-results';
import ProviderSearchSkeleton from './provider-search-skeleton';

const ProviderSearch = (): ReactElement => {
  const { t } = useTranslation();
  const headingId = useId();

  const urlSearchParams = useSearchParams();

  const {
    loadingResults,
    pagination,
    providers,
    searchCriteria,
    isLocationSelected,
  } = useSearch();

  const {
    value,
    isDesktop,
    previousRange,
    setPreviousRange,
    timeZone,
    t: translate,
    locale,
    lastFetchableDate,
    todaysDate,
    isOpen,
    toggleOpen,
    setIsOpen,
    timeStampHandler,
    onChange,
  } = useCalendarHook();

  const { featureFlags } = useFeatureFlags();
  if (loadingResults || !providers || !pagination) {
    return <ProviderSearchSkeleton />;
  }

  // Soft matches ("other scheduling options") are paginated alongside exact
  // matches, but the results count only reports providers that match the search.
  const matchingTotal = pagination.exactTotal ?? pagination.total;

  return (
    <section
      data-testid={'provider-search'}
      aria-labelledby={headingId}
      className={
        'outer-margin pb-3 pt-6 tablet:pb-24 tablet:pt-8 desktop:pb-24 desktop:pt-11'
      }
    >
      <h2 id={headingId} className={'sr-only'}>
        Providers
      </h2>
      <div className={'grid grid-cols-2 gap-4 desktop:grid-cols-12'}>
        <div
          data-testid={'provider-search-results-count'}
          className={
            'pb-2 text-body-small-semibold leading-[140%] tracking-[-0.28px] desktop:col-span-4 desktop:text-body-medium-semibold desktop:tracking-[-0.32px]'
          }
        >
          {loadingResults ? (
            <LoadingGradient className={'h-[1.625rem] w-[128px] rounded-sm'} />
          ) : (
            t('searchResults.foundProviders', { count: matchingTotal })
          )}
        </div>
        <div
          className={
            'grid justify-items-end desktop:col-span-4 desktop:justify-items-start'
          }
        >
          <SortSelect />
        </div>
        {!featureFlags.isSchedulingEnabled ? (
          <div className={'desktop:col-span-4'} />
        ) : (
          <ProviderSearchCalendar
            timeStampHandler={timeStampHandler}
            value={value}
            onChange={onChange}
            isDesktop={isDesktop}
            previousRange={previousRange}
            setPreviousRange={setPreviousRange}
            timeZone={timeZone}
            t={translate}
            locale={locale}
            lastFetchableDate={lastFetchableDate}
            todaysDate={todaysDate}
            isOpen={isOpen}
            toggleOpen={toggleOpen}
            setIsOpen={setIsOpen}
          />
        )}
        {/* Filters */}
        <ProviderSearchFilters
          isDisabled={loadingResults}
          isLocationSelected={isLocationSelected}
          isLoading={loadingResults}
          totalCount={matchingTotal}
        />
        {/* Provider list */}
        <div className={'col-span-2 desktop:col-span-8'}>
          <FilterBanner
            reasonForVisit={urlSearchParams.get('reason-for-visit')}
            providerGroup={urlSearchParams.get('provider-group')}
          />
          <ProviderSearchResults
            searchParams={searchCriteria}
            startDate={previousRange?.startDate}
            endDate={previousRange?.endDate}
          />
        </div>
      </div>
    </section>
  );
};

export { ProviderSearch };
